package com.trevorwiebe.timesheet.invoicing.data.dto

import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceLine
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceStatus
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable

@Serializable
data class InvoiceDto(
    val number: Int = 0,
    val customerId: String = "",
    val customerName: String = "",
    val paymentTerms: String = "",
    val invoiceDate: String = "",
    val status: String = InvoiceStatus.DRAFT.name,
    val sentAt: String? = null,
    val sentTo: String = "",
    val amountPaid: Int = 0,
    // Stored alongside the lines so server-side code (PDF, email) needn't recompute it.
    val total: Int = 0,
    val lines: List<InvoiceLineDto> = emptyList(),
    val pdfPath: String = "",
    val editedAt: String? = null,
)

@Serializable
data class InvoiceLineDto(
    val loadId: String = "",
    val loadNumber: String = "",
    val po: String = "",
    val date: String = "",
    val pickup: String = "",
    val drop: String = "",
    val description: String = "",
    val base: Int = 0,
    val fuelSurcharge: Int = 0,
    val detention: Int = 0,
    val stopOff: Int = 0,
    val other: Int = 0,
    val fuelSurchargePercent: Double = 0.0,
)

/** One document per invoiced load, keyed by load id; lets a transaction refuse double billing. */
@Serializable
data class InvoicedLoadDto(val invoiceId: String = "")

fun InvoiceDto.toInvoice(id: String): Invoice = Invoice(
    id = id,
    number = number,
    customerId = customerId,
    customerName = customerName,
    paymentTerms = paymentTerms,
    invoiceDate = LocalDate.parse(invoiceDate),
    lines = lines.map { it.toInvoiceLine() },
    status = InvoiceStatus.entries.firstOrNull { it.name == status } ?: InvoiceStatus.DRAFT,
    sentAt = sentAt?.let { LocalDate.parse(it) },
    sentTo = sentTo,
    amountPaid = amountPaid,
    pdfPath = pdfPath,
    editedAt = editedAt?.let { LocalDate.parse(it) },
)

fun Invoice.toInvoiceDto(): InvoiceDto = InvoiceDto(
    number = number,
    customerId = customerId,
    customerName = customerName,
    paymentTerms = paymentTerms,
    invoiceDate = invoiceDate.toString(),
    status = status.name,
    sentAt = sentAt?.toString(),
    sentTo = sentTo,
    amountPaid = amountPaid,
    total = total,
    lines = lines.map { it.toInvoiceLineDto() },
    pdfPath = pdfPath,
    editedAt = editedAt?.toString(),
)

private fun InvoiceLineDto.toInvoiceLine() = InvoiceLine(
    loadId = loadId,
    loadNumber = loadNumber,
    po = po,
    date = LocalDate.parse(date),
    pickup = pickup,
    drop = drop,
    description = description,
    base = base,
    fuelSurcharge = fuelSurcharge,
    detention = detention,
    stopOff = stopOff,
    other = other,
    fuelSurchargePercent = fuelSurchargePercent,
)

fun InvoiceLine.toInvoiceLineDto() = InvoiceLineDto(
    loadId = loadId,
    loadNumber = loadNumber,
    po = po,
    date = date.toString(),
    pickup = pickup,
    drop = drop,
    description = description,
    base = base,
    fuelSurcharge = fuelSurcharge,
    detention = detention,
    stopOff = stopOff,
    other = other,
    fuelSurchargePercent = fuelSurchargePercent,
)

@Serializable
data class InvoiceIdRequest(val invoiceId: String)

@Serializable
data class SendInvoiceRequest(val invoiceId: String, val to: List<String>, val documentUrls: List<String>)

@Serializable
data class GeneratePdfResponse(val pdfPath: String = "")

/** [sent] is false when the server skipped the email (it never emails from the emulator). */
@Serializable
data class SendInvoiceResponse(
    val sent: Boolean = false,
    val attachedDocuments: Int = 0,
    val skippedDocuments: List<String> = emptyList(),
)
